import { Card } from "antd";
import type { ReactNode } from "react";
import { holatVar } from "./palette";
import { Sparkline } from "./Sparkline";

export type KpiDirection = "up" | "down" | "flat";

export interface KpiTileDelta {
	/** Formatlangan o'zgarish — `"+1,8 p.p."`. Glif komponent tomonidan qo'shiladi. */
	text: string;
	direction: KpiDirection;
	/** Solishtiruv yorlig'i — `"Iyulga nisbatan"` (reja imzosidan kengaytirilgan). */
	label?: string;
}

export interface KpiTileProps {
	label: string;
	/** ⚠️ MATN, son emas — «92,4%» va «4 218 daq» kabi qiymatlar. */
	value: string;
	hint?: ReactNode;
	delta?: KpiTileDelta;
	spark?: { points: number[]; color: string };
	tone?: "default" | "danger";
}

/**
 * ⚠️ Glif MAJBURIY — yo'nalish rang bilan YOLG'IZ berilmaydi
 * (spec §2.1, `DayStrip.tsx` qoidasi).
 */
const GLIF: Record<KpiDirection, string> = { up: "▲", down: "▼", flat: "■" };

/**
 * KPI kartasi — yorliq, katta qiymat, delta, izoh va ixtiyoriy sparkline.
 *
 * ⚠️⚠️ **Delta rangi inline `style` da EMAS, `chart-theme.css` dagi
 * `[data-kpi-direction]` qoidasida.** Inline `var(--…)` ni happy-dom
 * qanday serializatsiya qilishi kafolatlanmagan, hex esa dark rejimni
 * buzardi. Test `data-direction` ni tekshiradi.
 *
 * ⚠️ **Yashil YO'Q** — o'sish ko'k (`kelgan`), pasayish qizil
 * (`kelmagan`): yashil↔qizil deuteranopiyada ajralmaydi (`theme.ts`).
 *
 * ⚠️ Primitiv `useTranslation` ga TEGMAYDI — barcha matn tashqaridan.
 */
export function KpiTile({ label, value, hint, delta, spark, tone = "default" }: KpiTileProps) {
	return (
		<Card size="small" className="h-full">
			<div data-tone={tone}>
				<div data-kpi-label className="text-xs" style={{ color: "var(--chart-axis)" }}>
					{label}
				</div>

				<div className="mt-0.5 flex items-end justify-between gap-3">
					<div
						data-kpi-value
						className="font-semibold text-2xl tabular-nums leading-tight"
						style={{ color: tone === "danger" ? holatVar("kelmagan") : "var(--chart-ink)" }}
					>
						{value}
					</div>

					{spark ? (
						<div className="min-w-0 max-w-[45%] flex-1">
							<Sparkline
								points={spark.points}
								color={spark.color}
								width={96}
								height={28}
								area
								ariaLabel={label}
							/>
						</div>
					) : null}
				</div>

				{delta ? (
					<div data-kpi-delta-row className="mt-0.5 text-xs">
						{delta.label ? (
							<span style={{ color: "var(--chart-axis)" }}>{`${delta.label} `}</span>
						) : null}
						<span
							data-testid="kpi-delta"
							data-direction={delta.direction}
							data-kpi-direction={delta.direction}
							className="font-semibold tabular-nums"
						>
							{`${GLIF[delta.direction]} ${delta.text}`}
						</span>
					</div>
				) : null}

				{hint ? (
					<div className="mt-0.5 text-xs" style={{ color: "var(--chart-axis)" }}>
						{hint}
					</div>
				) : null}
			</div>
		</Card>
	);
}
